import {
    Alert,
    Box,
    Button,
    Divider,
    Flex,
    FormLabel,
    Modal,
    ModalBody,
    ModalFooter,
    Text,
    ThemeColorVariable,
    ThemeFontSizeVariable,
    ThemeSpaceVariable,
} from "@caspeco/casper-ui-library";
import { useAvailableTimes } from "api/api-hooks/use-available-times";
import { useExperienceView } from "api/api-hooks/use-experience-view";
import { useExternalBookingSettings } from "api/api-hooks/use-external-booking-settings";
import {
    Days,
    getDayStatesQueryKey,
    getGroupSizeLimitsForDate,
} from "api/api-hooks/use-day-states";
import { QuantityInput } from "components/quantity-input/quantity-input";
import { SafeHTML } from "components/safe-html";
import { SelectDate } from "components/selection-components/select-date/select-date";
import { getFirstUpcomingActiveDate } from "components/selection-components/select-date/select-date-utils";
import { useBookingActionsContext, useBookingStateContext } from "hooks/use-booking-state";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Routes } from "routes";
import { ExperienceViewExperienceItem, isExperienceItem } from "types/experience-view";
import { UrlParamName } from "search-params";
import { shouldDisableDate } from "utils/datepicker-utils";

interface ExperienceViewFilterModalProps {
    isOpen: boolean;
    onClose: () => void;
    sectionId?: number;
    experienceId?: number;
}

function ExperienceViewFilterModal({
    isOpen,
    onClose,
    sectionId,
    experienceId,
}: ExperienceViewFilterModalProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const state = useBookingStateContext();
    const { setMultipleStateProps } = useBookingActionsContext();
    const externalBookingSettings = useExternalBookingSettings().data;
    const { activateWebChildren, childrenAge, maxWebGuests, messageExceededGuests } =
        externalBookingSettings?.unitsMetaData ?? {};
    const activateInquiry = externalBookingSettings?.activateInquiry ?? false;

    const { data: experienceViewItems } = useExperienceView(experienceId !== undefined);

    const defaultGuests = externalBookingSettings?.defaultAmountGuests ?? 2;
    const [localAdults, setLocalAdults] = useState(
        (state.guests ?? defaultGuests) - (state.guestsChildren ?? 0)
    );
    const [localChildren, setLocalChildren] = useState(state.guestsChildren ?? 0);
    const [localDate, setLocalDate] = useState<Date | undefined>(undefined);

    useAvailableTimes(localDate ? moment(localDate) : moment());

    const queryClient = useQueryClient();
    const dayStateLimits = useMemo(() => {
        if (!localDate) return null;
        // The experience view never runs in booking-request mode
        const monthData = queryClient.getQueryData<Days>(getDayStatesQueryKey(localDate, false));
        if (!monthData) return null;
        const dateKey = moment(localDate).format("YYYY-MM-DD");
        const { min, max } = getGroupSizeLimitsForDate(monthData, dateKey, experienceId, sectionId);
        return min !== null || max !== null ? { min, max } : null;
    }, [localDate, experienceId, sectionId, queryClient]);

    const selectedExperience = useMemo(
        () =>
            experienceId !== undefined
                ? experienceViewItems?.find(
                      (item): item is ExperienceViewExperienceItem =>
                          isExperienceItem(item) && item.id === experienceId
                  )
                : undefined,
        [experienceId, experienceViewItems]
    );
    const firstUpcomingExperienceActiveDate = useMemo(() => {
        if (selectedExperience === undefined) {
            return null;
        }

        return getFirstUpcomingActiveDate(selectedExperience.activeDates ?? []);
    }, [selectedExperience]);

    /**
     * `selection` carries the guests and date the modal has just applied. The state update that
     * writes them to the URL has not landed yet, so `location.search` would still hold the previous
     * values — they are set explicitly here instead (BOOK-3911).
     */
    const handleBookingRequestRedirect = (selection?: {
        guests: number;
        guestsChildren?: number;
        date?: Date;
    }) => {
        const requestPath = location.pathname.replace(
            Routes.EXPERIENCE_VIEW,
            Routes.BOOKING_REQUEST
        );
        const params = new URLSearchParams(location.search);
        if (experienceId !== undefined) {
            params.set(UrlParamName.EXPERIENCE_ID, String(experienceId));
        } else {
            params.delete(UrlParamName.EXPERIENCE_ID);
        }
        if (selection) {
            params.set(UrlParamName.GUESTS, String(selection.guests));
            if (selection.guestsChildren !== undefined) {
                params.set(UrlParamName.GUESTS_CHILDREN, String(selection.guestsChildren));
            }
            if (selection.date) {
                params.set(UrlParamName.DATE, moment(selection.date).format("YYYY-MM-DD"));
            }
        }
        // The wizard opens on its date step, which is a screen asking for the date the guest has
        // just chosen. With one in hand, go straight to picking a time instead. Without one — the
        // plain "N or more guests" link — the wizard still needs to ask.
        const newPath = selection?.date ? `${requestPath}/${Routes.AVAILABILITY}` : requestPath;
        onClose();
        void navigate({
            pathname: newPath,
            search: params.toString(),
        });
    };

    const hasInitializedExperienceRef = useRef(false);
    const localGuestsRef = useRef({ adults: localAdults, children: localChildren });
    localGuestsRef.current = { adults: localAdults, children: localChildren };

    useEffect(() => {
        if (!isOpen || experienceId === undefined) {
            return;
        }

        hasInitializedExperienceRef.current = false;
    }, [experienceId, isOpen]);

    useEffect(() => {
        if (isOpen) {
            const children = state.guestsChildren ?? 0;
            setLocalAdults((state.guests ?? defaultGuests) - children);
            setLocalChildren(children);
            setLocalDate(state.date?.toDate() ?? undefined);
        }
    }, [defaultGuests, isOpen, state.date, state.guests, state.guestsChildren]);

    useEffect(() => {
        if (
            selectedExperience === undefined ||
            !isOpen ||
            experienceId === undefined ||
            hasInitializedExperienceRef.current ||
            !firstUpcomingExperienceActiveDate ||
            state.date ||
            (externalBookingSettings &&
                shouldDisableDate(firstUpcomingExperienceActiveDate, externalBookingSettings))
        ) {
            return;
        }

        hasInitializedExperienceRef.current = true;

        setLocalDate(firstUpcomingExperienceActiveDate);
        setMultipleStateProps?.({
            selectedMonth: moment(firstUpcomingExperienceActiveDate).startOf("month").toDate(),
        });
    }, [
        experienceId,
        selectedExperience,
        externalBookingSettings,
        firstUpcomingExperienceActiveDate,
        isOpen,
        setMultipleStateProps,
        state.date,
    ]);

    useEffect(() => {
        if (!dayStateLimits) return;
        const { min, max } = dayStateLimits;
        const { adults, children } = localGuestsRef.current;
        const total = adults + children;
        if (min !== null && total < min) {
            setLocalAdults(Math.max(1, min - children));
        } else if (max !== null && total > max) {
            setLocalAdults(Math.max(1, max - children));
        }
    }, [dayStateLimits]);

    const isRequestOnlyExperience = Boolean(selectedExperience?.bookingRequestOnly);

    const handleApply = () => {
        const guestsChildren = activateWebChildren ? localChildren : undefined;

        // Nothing to book: the only way on for a request-only experience is the request flow, so
        // applying takes the guest straight there rather than back to a modal with no times.
        //
        // Deliberately without setMultipleStateProps: that pushes its own history entry for the
        // experience-view URL, so applying would leave two entries behind and the browser's Back
        // would land the guest on the page they just left rather than where they came from. The
        // redirect carries guests and date in the URL, which the store hydrates from on arrival.
        if (isRequestOnlyExperience) {
            handleBookingRequestRedirect({
                guests: localAdults + localChildren,
                guestsChildren,
                date: localDate,
            });
            return;
        }

        setMultipleStateProps?.({
            guests: localAdults + localChildren,
            guestsChildren,
            date: localDate ? moment(localDate) : undefined,
        });
        onClose();
    };

    const childrenDescription = childrenAge
        ? t("experienceView.childrenAged", { age: childrenAge })
        : undefined;
    const bookingRequestAmount = maxWebGuests ? maxWebGuests + 1 : 0;
    // The online-booking limit does not apply to a request: the venue answers it by hand.
    const guestLimit = isRequestOnlyExperience ? undefined : maxWebGuests;
    const totalGuests = activateWebChildren ? localAdults + localChildren : localAdults;
    const atMaxGuests = !!guestLimit && totalGuests >= guestLimit;
    const adultsLabel = activateWebChildren ? t("adults") : t("experienceView.guests");
    const maxAdults = guestLimit
        ? guestLimit - (activateWebChildren ? localChildren : 0)
        : undefined;
    const maxChildren = guestLimit ? guestLimit - localAdults : undefined;
    const showExceededGuestsMessage =
        (!activateInquiry || (experienceId && !selectedExperience?.bookingRequestEnabled)) &&
        atMaxGuests &&
        messageExceededGuests;
    return (
        <Modal isOpen={isOpen} onClose={onClose} size="md" closeOnOverlayClick={true}>
            <ModalBody overflow="auto" flex={1} px={ThemeSpaceVariable.Medium}>
                <Flex
                    py={ThemeSpaceVariable.Medium}
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Box>
                        <FormLabel m="0" fontWeight="medium" fontSize={ThemeFontSizeVariable.Large}>
                            {adultsLabel}
                        </FormLabel>
                        {/* The link is the "you can also ask instead" escape hatch from booking.
                            A request-only experience has no booking to escape from, and applying
                            already leads to the request flow — so it would be a second, confusing
                            route to the same place (BOOK-3911). */}
                        {activateInquiry &&
                            !isRequestOnlyExperience &&
                            (experienceId === undefined ||
                                selectedExperience?.bookingRequestEnabled) && (
                                <Text>
                                    <Trans
                                        i18nKey="bookingRequest.createRequestWithLink"
                                        values={{ numberOfGuests: bookingRequestAmount }}
                                        components={{
                                            1: (
                                                <Box
                                                    as="button"
                                                    onClick={() => handleBookingRequestRedirect()}
                                                    style={{
                                                        cursor: "pointer",
                                                        textDecoration: "underline",
                                                    }}
                                                />
                                            ),
                                        }}
                                    />
                                </Text>
                            )}
                    </Box>
                    <QuantityInput
                        quantity={localAdults}
                        onChange={setLocalAdults}
                        min={1}
                        max={maxAdults}
                        inputAriaLabel={adultsLabel}
                        decreaseAriaLabel={t("decrease")}
                        increaseAriaLabel={t("increase")}
                    />
                </Flex>

                {activateWebChildren && (
                    <>
                        <Divider />
                        <Flex
                            py={ThemeSpaceVariable.Medium}
                            justifyContent="space-between"
                            alignItems="center"
                        >
                            <Box>
                                <FormLabel
                                    m="0"
                                    fontWeight="medium"
                                    fontSize={ThemeFontSizeVariable.Large}
                                >
                                    {t("children")}
                                </FormLabel>
                                {childrenDescription && (
                                    <Text color={ThemeColorVariable.OnBackgroundSubdued}>
                                        {childrenDescription}
                                    </Text>
                                )}
                            </Box>
                            <QuantityInput
                                quantity={localChildren}
                                onChange={setLocalChildren}
                                min={0}
                                max={maxChildren}
                                inputAriaLabel={t("children")}
                                decreaseAriaLabel={t("decrease")}
                                increaseAriaLabel={t("increase")}
                            />
                        </Flex>
                    </>
                )}
                <Divider />

                {dayStateLimits && (
                    <Box mt={ThemeSpaceVariable.Small}>
                        <Text color={ThemeColorVariable.OnBackgroundSubdued}>
                            {dayStateLimits.min !== null && dayStateLimits.max !== null
                                ? t("experienceView.groupSizeMinMaxLimit", {
                                      min: dayStateLimits.min,
                                      max: dayStateLimits.max,
                                  })
                                : dayStateLimits.min !== null
                                  ? t("experienceView.groupSizeMinLimit", {
                                        min: dayStateLimits.min,
                                    })
                                  : t("experienceView.groupSizeMaxLimit", {
                                        max: dayStateLimits.max,
                                    })}
                        </Text>
                    </Box>
                )}

                {showExceededGuestsMessage && (
                    <Box mt={ThemeSpaceVariable.Medium}>
                        <Alert type="info" iconVerticalAlign="flex-start">
                            <SafeHTML element="div" html={messageExceededGuests} />
                        </Alert>
                    </Box>
                )}

                {isRequestOnlyExperience && (
                    <Box mt={ThemeSpaceVariable.Medium}>
                        <Alert type="info" iconVerticalAlign="flex-start">
                            {t("experienceView.requestOnly")}
                        </Alert>
                    </Box>
                )}

                <Box pt={ThemeSpaceVariable.Medium}>
                    <SelectDate
                        onChange={setLocalDate}
                        date={localDate}
                        sectionId={sectionId}
                        experienceId={experienceId}
                        guestsOverride={totalGuests}
                        requestOnly={isRequestOnlyExperience}
                        informationOnly={Boolean(selectedExperience?.informationOnly)}
                    />
                </Box>
            </ModalBody>

            <ModalFooter
                px={ThemeSpaceVariable.Medium}
                pt={ThemeSpaceVariable.Medium}
                pb={{ base: ThemeSpaceVariable.XLarge, md: ThemeSpaceVariable.Medium }}
            >
                <Flex gap={ThemeSpaceVariable.Medium} w="full" justifyContent="flex-end">
                    <Button size="xl" variant="ghost" onClick={onClose}>
                        {t("cancel")}
                    </Button>
                    <Button
                        variant="primary"
                        size="xl"
                        onClick={handleApply}
                        isDisabled={!localDate || totalGuests === 0}
                    >
                        {t("apply")}
                    </Button>
                </Flex>
            </ModalFooter>
        </Modal>
    );
}

export { ExperienceViewFilterModal };
